// ============================================================
// TERRAIN — special battlefield cells
// ============================================================
//
// The 8×8 battlefield can have terrain features that modify combat,
// movement, and spell behaviour. Layout is deterministic — generated
// from a u32 seed so server and both PvP clients see the SAME map.
//
//   - 'grass'         : (a-2) Archer damage against units standing on
//                       this cell is -50% (cover). Fire / Fireball
//                       ignites it (→ 'burningGrass').
//   - 'burningGrass'  : (a-2) keeps the archer cover effect. In addition
//                       each tick deals magical DoT to anyone standing on
//                       it. Persists for the rest of the battle.
//   - 'puddle'        : (b)  Cavalry has -1 movement steps for any tick
//                       in which it touches this cell. Lightning damage
//                       on a unit standing in a puddle is +30%.
//   - 'swamp'         : (Session 11) the "grass + puddle" composite. Born
//                       from Grow/Rain casting on the opposite terrain
//                       (Grow on puddle → swamp; Rain on grass → swamp).
//                       Slows ALL units (not just cavalry): Infantry /
//                       Archer (speed=1) alternate move-skip-move-skip
//                       across consecutive ticks they stand on swamp
//                       (= half speed); Cavalry (speed=2) gets budget=1
//                       (same as puddle behaviour). Swamp itself can be
//                       converted back: Grow on swamp → grass; Rain on
//                       swamp → puddle.
//   - 'boulder'       : (Session 12, Leader CCC Rockfall) impassable
//                       block placed by the Rockfall spell on an empty
//                       target cell. Movement auto-detours single-axis
//                       around boulders. Grow/Rain never transition a
//                       boulder (transition tables return null). Fire
//                       (Fireball) and Lightning shatter boulders in
//                       their spell radius back to 'none' (the spell's
//                       damage effect carries `breakBoulder: true`).
//
// Layout rules (designer-chosen — see spec §2.8 / §5.5):
//   - Terrain only appears in rows 2..5 (middle four rows). Rows 0..1
//     are the AI deploy zone, rows 6..7 are the player deploy zone.
//   - Each battle has exactly 2 grass clusters + 2 puddle clusters.
//   - Each cluster is grown by a random walk targeting 3..5 connected
//     cells (Manhattan adjacency). Smaller clusters are accepted if the
//     walk runs out of neighbours.
//   - Clusters never overlap. Saturation is impossible in practice
//     (4×5 = 20 max cells, band has 4×8 = 32 cells).
//
// All `xxx` cells visible to the client during DEPLOY are computed from
// the SAME seed the server stores in `state.terrainSeed`. The server
// remains the authority for terrain effects during EXEC; the client
// only reads its local copy for display + input UX.

import { SeededRng } from './rng.ts';
import { BATTLE_COLS, BATTLE_ROWS } from '../constants.ts';
// 平衡性數字 (TERRAIN_*) 搬到 balance.ts；這裡 re-export 維持 back-compat。
import {
  TERRAIN_MIN_Y,
  TERRAIN_MAX_Y,
  TERRAIN_CLUSTER_SIZE_MIN,
  TERRAIN_CLUSTER_SIZE_MAX,
  TERRAIN_GRASS_CLUSTERS,
  TERRAIN_PUDDLE_CLUSTERS,
} from '../balance.ts';

export type TerrainCell = 'none' | 'grass' | 'burningGrass' | 'puddle' | 'swamp' | 'boulder';

/** 8×8 grid, indexed `[y][x]`. */
export type TerrainGrid = TerrainCell[][];

// ============================================================
// Session 11 follow-up — Grow / Rain transition tables
// ============================================================
//
// Grow / Rain are dual-action: they convert terrain in their radius
// according to these tables, AND apply a per-unit effect branched on the
// PRE-paint terrain. Putting the tables here (next to `TerrainCell`)
// keeps the transition rules colocated with the type definition so the
// only place to look for "what does Grow turn X into?" is right below.
//
// Returns the NEW terrain for the cell, or `null` if Grow/Rain doesn't
// convert this terrain (cell stays as-is). `null` covers:
//   - Grow on 'grass'        → unchanged (root effect on unit, no terrain change)
//   - Grow on 'burningGrass' → unchanged (Fire+Grow combo intentional design)
//   - Rain on 'puddle'       → unchanged (flood damage on unit, no terrain change)
//   - Rain on 'burningGrass' → unchanged (preserve Fire+Rain interaction TBD)
//
// `'swamp'` is the composite produced when Grow lands on puddle (water +
// vegetation = swamp) and when Rain lands on grass (grass + water = swamp).
// Casting the opposite spell on swamp reverts to one of the two base
// terrains: Grow on swamp → grass (vegetation overgrows the water);
// Rain on swamp → puddle (water washes out the vegetation).

/** Transition `current` → new cell when Grow hits it. `null` = no change. */
export function growTransition(current: TerrainCell): TerrainCell | null {
  switch (current) {
    case 'none':    return 'grass';   // empty cell → plant grass
    case 'puddle':  return 'swamp';   // puddle + grow → swamp
    case 'swamp':   return 'grass';   // swamp + grow → grass (vegetation wins)
    // 'grass' / 'burningGrass' / 'boulder' → no terrain change
    // (Session 12 — boulder explicitly immune to Grow per design)
    case 'boulder': return null;
    default: return null;
  }
}

/** Transition `current` → new cell when Rain hits it. `null` = no change. */
export function rainTransition(current: TerrainCell): TerrainCell | null {
  switch (current) {
    case 'none':         return 'puddle';  // empty cell → flood puddle
    case 'grass':        return 'swamp';   // grass + rain → swamp
    case 'swamp':        return 'puddle';  // swamp + rain → puddle (water wins)
    case 'burningGrass': return 'none';    // rain extinguishes fire + washes
                                           // away ash → bare ground. Note:
                                           // unit standing on it still gets
                                           // the per-unit "drizzle" branch
                                           // because the per-unit check
                                           // reads PRE-paint terrain
                                           // (= 'burningGrass' = non-puddle).
    // 'puddle' → no terrain change (still triggers per-unit "drown bonus")
    // Session 12 — boulder explicitly immune to Rain.
    case 'boulder':      return null;
    default: return null;
  }
}

// Re-export for back-compat. 想改數值請編輯 src/balance.ts。
export {
  TERRAIN_MIN_Y,
  TERRAIN_MAX_Y,
  TERRAIN_CLUSTER_SIZE_MIN,
  TERRAIN_CLUSTER_SIZE_MAX,
  TERRAIN_GRASS_CLUSTERS,
  TERRAIN_PUDDLE_CLUSTERS,
};

/** Build an empty terrain grid (all 'none'). */
export function emptyTerrain(): TerrainGrid {
  const g: TerrainGrid = [];
  for (let y = 0; y < BATTLE_ROWS; y++) {
    const row: TerrainCell[] = [];
    for (let x = 0; x < BATTLE_COLS; x++) row.push('none');
    g.push(row);
  }
  return g;
}

/** Bounds-checked terrain read. Out-of-range returns `'none'`. */
export function getTerrainAt(g: TerrainGrid | null | undefined, x: number, y: number): TerrainCell {
  if (!g) return 'none';
  if (y < 0 || y >= BATTLE_ROWS) return 'none';
  if (x < 0 || x >= BATTLE_COLS) return 'none';
  return g[y][x];
}

function inMiddleRows(x: number, y: number): boolean {
  if (x < 0 || x >= BATTLE_COLS) return false;
  if (y < TERRAIN_MIN_Y || y > TERRAIN_MAX_Y) return false;
  return true;
}

/**
 * Random-walk expansion from a random empty middle-row start cell.
 * Returns up to `targetSize` connected (Manhattan-adjacent) cells.
 * Stops early if no further free neighbour exists.
 */
function growCluster(
  rng: SeededRng,
  grid: TerrainGrid,
  targetSize: number,
): { x: number; y: number }[] {
  // 1. Collect all empty middle-row cells.
  const empties: { x: number; y: number }[] = [];
  for (let y = TERRAIN_MIN_Y; y <= TERRAIN_MAX_Y; y++) {
    for (let x = 0; x < BATTLE_COLS; x++) {
      if (grid[y][x] === 'none') empties.push({ x, y });
    }
  }
  if (empties.length === 0) return [];

  // 2. Pick a random start cell.
  const start = empties[rng.nextInt(0, empties.length - 1)];
  const cluster: { x: number; y: number }[] = [start];
  const inCluster = new Set<number>([start.y * BATTLE_COLS + start.x]);

  // 3. Expand by picking a random free neighbour each step.
  while (cluster.length < targetSize) {
    const candidates: { x: number; y: number }[] = [];
    const seen = new Set<number>();
    for (const c of cluster) {
      const NEIGHBOURS = [[-1, 0], [1, 0], [0, -1], [0, 1]];
      for (const [dx, dy] of NEIGHBOURS) {
        const nx = c.x + dx;
        const ny = c.y + dy;
        if (!inMiddleRows(nx, ny)) continue;
        if (grid[ny][nx] !== 'none') continue;
        const key = ny * BATTLE_COLS + nx;
        if (inCluster.has(key) || seen.has(key)) continue;
        seen.add(key);
        candidates.push({ x: nx, y: ny });
      }
    }
    if (candidates.length === 0) break;
    const pick = candidates[rng.nextInt(0, candidates.length - 1)];
    cluster.push(pick);
    inCluster.add(pick.y * BATTLE_COLS + pick.x);
  }
  return cluster;
}

/**
 * Generate a deterministic terrain grid from a u32 seed.
 *
 * Output: an 8×8 grid with exactly 2 grass + 2 puddle clusters in rows
 * 2..5; deploy rows (0..1, 6..7) are always `'none'`. Same seed → same
 * grid byte-for-byte (used as a basic anti-desync invariant in tests).
 *
 * If saturation prevents placing all four clusters (statistically near
 * impossible), the function silently emits fewer rather than throwing —
 * battle stays playable, you just get a slightly emptier map. Tests
 * assert that 4 clusters always land for the canonical seed range.
 */
export function generateTerrain(seed: number): TerrainGrid {
  const rng = new SeededRng(seed >>> 0);
  const grid = emptyTerrain();

  // Interleave so grass and puddle alternate (visual balance — avoids
  // both grass clusters bunching first when the band is still empty).
  const placement: TerrainCell[] = ['grass', 'puddle', 'grass', 'puddle'];

  for (const kind of placement) {
    let placed = false;
    // Up to 8 retries; if we draw a tiny cluster (rare), pick a new start.
    for (let attempt = 0; attempt < 8 && !placed; attempt++) {
      const target = rng.nextInt(TERRAIN_CLUSTER_SIZE_MIN, TERRAIN_CLUSTER_SIZE_MAX);
      const cluster = growCluster(rng, grid, target);
      if (cluster.length >= TERRAIN_CLUSTER_SIZE_MIN) {
        for (const c of cluster) grid[c.y][c.x] = kind;
        placed = true;
      }
    }
    // Saturation fallback: drop this cluster silently. (See doc above.)
  }

  return grid;
}

/**
 * Flip a terrain grid 180° (used by PvP team-1 client to convert world
 * coords to view coords). Self-inverse, mirrors `Game.viewFlipX/Y`.
 */
export function flipTerrainForView(g: TerrainGrid): TerrainGrid {
  const out = emptyTerrain();
  for (let y = 0; y < BATTLE_ROWS; y++) {
    for (let x = 0; x < BATTLE_COLS; x++) {
      out[BATTLE_ROWS - 1 - y][BATTLE_COLS - 1 - x] = g[y][x];
    }
  }
  return out;
}
